import { cn } from "@/lib/utils/cn";
import React from "react";

type LoaderProps = {
  size?: "xs" | "sm" | "md" | "lg";
  variant?: "default" | "dots" | "pulse" | "orbit";
  className?: string;
};

const Loader: React.FC<LoaderProps> = ({ className, size = "md", variant = "default" }) => {
  const sizeClasses = {
    xs: "w-3 h-3",
    sm: "w-4 h-4",
    md: "w-6 h-6",
    lg: "w-8 h-8",
  };

  const dotSizes = {
    xs: "w-1 h-1",
    sm: "w-1 h-1",
    md: "w-1.5 h-1.5",
    lg: "w-2 h-2",
  };

  if (variant === "dots") {
    return (
      <div className={cn("flex items-center justify-center space-x-1", className)}>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={cn("bg-primary rounded-full animate-pulse", dotSizes[size])}
            style={{
              animationDelay: `${i * 0.2}s`,
              animationDuration: "1.4s",
            }}
          />
        ))}
      </div>
    );
  }

  if (variant === "pulse") {
    return (
      <div className={cn("relative", sizeClasses[size], className)}>
        <div className="absolute inset-0 bg-primary/20 rounded-full animate-ping" />
        <div
          className="absolute inset-0 bg-primary/40 rounded-full animate-ping"
          style={{ animationDelay: "0.2s" }}
        />
        <div className="absolute inset-0 bg-primary rounded-full animate-pulse" />
      </div>
    );
  }

  if (variant === "orbit") {
    return (
      <div className={cn("relative", sizeClasses[size], className)}>
        <div className="absolute inset-0 rounded-full border-2 border-primary/20" />
        <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-primary animate-spin" />
        <div
          className="absolute inset-1 rounded-full border border-transparent border-t-primary/60 animate-spin"
          style={{
            animationDirection: "reverse",
            animationDuration: "0.7s",
          }}
        />
        <div className="absolute inset-2 bg-primary/10 rounded-full animate-pulse" />
      </div>
    );
  }

  return (
    // Ring tones use `currentColor`, with a `text-primary` default — so a bare
    // <Loader> stays brand-primary, but callers can recolor via `className`
    // (e.g. text-amber-600, text-muted-foreground) like a lucide spinner.
    <div className={cn("relative text-primary", sizeClasses[size], className)}>
      <div className="absolute inset-0 rounded-full border-2 border-current/10" />
      <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-current border-r-current/60 animate-spin" />
      <div
        className="absolute inset-0.5 rounded-full border border-transparent border-t-current/40 animate-spin"
        style={{
          animationDirection: "reverse",
          animationDuration: "0.7s",
        }}
      />
    </div>
  );
};

const AppLoader: React.FC = () => {
  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="flex flex-col items-center space-y-4">
        <Loader variant="orbit" size="lg" />
        <div className="text-center space-y-1">
          <p className="text-sm font-medium text-foreground animate-pulse">Loading...</p>
          <div className="flex space-x-1 justify-center">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="w-1 h-1 bg-primary/60 rounded-full animate-bounce"
                style={{
                  animationDelay: `${i * 0.1}s`,
                  animationDuration: "1s",
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const ContentLoader: React.FC<{ className?: string; }> = ({ className }) => {
  return (
    <div className={cn("flex items-center justify-center py-8", className)}>
      <div className="flex flex-col items-center space-y-3">
        <Loader variant="default" size="md" />
        <p className="text-sm text-muted-foreground animate-pulse">Loading content...</p>
      </div>
    </div>
  );
};

export { AppLoader, ContentLoader, Loader };
